import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, MapPinIcon, PhoneIcon } from 'lucide-react';
const AREAS = ['Paterson', 'Passaic', 'Clifton', 'Wayne', 'Hackensack', 'Paramus', 'Teaneck', 'Englewood', 'Ridgewood', 'Fair Lawn', 'Garfield', 'Lodi', 'Saddle Brook', 'Elmwood Park', 'Rutherford', 'East Rutherford', 'Lyndhurst', 'Kearny', 'Harrison', 'Newark', 'Bloomfield', 'Montclair', 'Glen Ridge', 'Nutley', 'Belleville'];
// Animated coverage dots
const COVERAGE_DOTS = [{
  top: '20%',
  left: '30%',
  delay: 0
}, {
  top: '35%',
  left: '65%',
  delay: 0.4
}, {
  top: '55%',
  left: '45%',
  delay: 0.8
}, {
  top: '70%',
  left: '25%',
  delay: 1.2
}, {
  top: '25%',
  left: '75%',
  delay: 0.6
}, {
  top: '65%',
  left: '70%',
  delay: 1.0
}, {
  top: '80%',
  left: '55%',
  delay: 1.4
}, {
  top: '15%',
  left: '50%',
  delay: 0.2
}];
export function ServiceArea() {
  return <section id="service-areas" className="relative py-24 bg-gray-50 overflow-hidden" data-id="element-309">
      {/* Subtle background */}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(ellipse at 80% 50%, rgba(11,122,196,0.05) 0%, transparent 60%)'
    }} data-id="element-310" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-311">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-312">
          {/* Left: Areas List */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-313">
            <p className="text-[#0B7AC4] font-bold uppercase tracking-widest text-sm mb-3" data-id="element-314">
              Coverage
            </p>
            <h2 className="text-4xl md:text-5xl font-black text-[#0A1628] mb-4" data-id="element-315">
              Serving All of North Jersey
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-[#0B7AC4] to-[#7AC943] rounded-full mb-8" data-id="element-316" />
            <p className="text-lg text-gray-500 mb-10 leading-relaxed" data-id="element-317">
              Fast, reliable service to homeowners throughout Passaic, Bergen,
              and Essex counties. We typically respond within 24 hours.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-10" data-id="element-318">
              {AREAS.map((area, index) => <motion.div key={area} initial={{
              opacity: 0,
              x: -10
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.04,
              duration: 0.4
            }} className="flex items-center gap-2 group" data-id="element-319">
                  <CheckCircle2Icon className="w-4 h-4 text-[#7AC943] flex-shrink-0 group-hover:scale-110 transition-transform" data-id="element-320" />
                  <span className="text-gray-600 font-medium text-sm group-hover:text-[#0B7AC4] transition-colors" data-id="element-321">
                    {area}
                  </span>
                </motion.div>)}
            </div>

            {/* CTA card */}
            <div className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100 flex items-center gap-4" data-id="element-322">
              <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{
              background: 'rgba(255,212,0,0.15)',
              border: '1.5px solid rgba(255,212,0,0.4)'
            }} data-id="element-323">
                <MapPinIcon className="w-6 h-6 text-[#0A1628]" data-id="element-324" />
              </div>
              <div className="flex-1" data-id="element-325">
                <h4 className="font-black text-[#0A1628] text-base" data-id="element-326">
                  Don&apos;t see your area?
                </h4>
                <p className="text-gray-500 text-sm" data-id="element-327">
                  We often travel outside our primary zone.
                </p>
              </div>
              <a href="tel:+18554711600" className="flex-shrink-0 bg-[#0A1628] text-white px-4 py-2.5 rounded-full font-bold text-sm hover:bg-[#0B7AC4] transition-colors flex items-center gap-1.5" data-id="element-328">
                <PhoneIcon className="w-3.5 h-3.5" data-id="element-329" /> Call Us
              </a>
            </div>
          </motion.div>

          {/* Right: Stylized Map Visual */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.92
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          ease: [0.22, 1, 0.36, 1]
        }} className="relative h-[520px] w-full rounded-3xl overflow-hidden shadow-2xl" style={{
          background: 'linear-gradient(135deg, #0A1628 0%, #0d1f3c 100%)',
          border: '1px solid rgba(11,122,196,0.2)'
        }} data-id="element-330">
            {/* Animated dot grid */}
            <div className="absolute inset-0 dot-grid-bg opacity-30" data-id="element-331" />

            {/* Subtle radial glow from center */}
            <div className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(circle at 50% 50%, rgba(11,122,196,0.2) 0%, transparent 65%)'
          }} data-id="element-332" />

            {/* Coverage dots */}
            {COVERAGE_DOTS.map((dot, i) => <div key={i} className="absolute" style={{
            top: dot.top,
            left: dot.left,
            animationDelay: `${dot.delay}s`
          }} data-id="element-333">
                <div className="w-2.5 h-2.5 rounded-full bg-[#0B7AC4] animate-dot-pulse" style={{
              animationDelay: `${dot.delay}s`
            }} data-id="element-334" />
              </div>)}

            {/* Radar rings */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-335">
              <div className="absolute w-28 h-28 -translate-x-1/2 -translate-y-1/2 border-2 border-[#0B7AC4]/60 rounded-full animate-radar" data-id="element-336" />
              <div className="absolute w-28 h-28 -translate-x-1/2 -translate-y-1/2 border-2 border-[#7AC943]/40 rounded-full animate-radar-delayed" data-id="element-337" />
            </div>

            {/* Center pin */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 flex flex-col items-center" data-id="element-338">
              <div className="w-16 h-16 rounded-full flex items-center justify-center shadow-2xl animate-float mb-4" style={{
              background: '#FFD400',
              boxShadow: '0 0 40px rgba(255,212,0,0.5), 0 0 80px rgba(255,212,0,0.2)'
            }} data-id="element-339">
                <MapPinIcon className="w-8 h-8 text-[#0A1628]" data-id="element-340" />
              </div>
              <div className="px-5 py-3 rounded-2xl text-center" style={{
              background: 'rgba(255,255,255,0.08)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,255,255,0.15)'
            }} data-id="element-341">
                <p className="text-white font-black text-lg" data-id="element-342">Paterson, NJ</p>
                <p className="text-[#7AC943] font-semibold text-xs tracking-wider uppercase" data-id="element-343">
                  Headquarters
                </p>
              </div>
            </div>

            {/* Corner labels */}
            <div className="absolute top-6 left-6 text-xs font-bold text-[#0B7AC4]/60 uppercase tracking-widest" data-id="element-344">
              Bergen Co.
            </div>
            <div className="absolute top-6 right-6 text-xs font-bold text-[#7AC943]/60 uppercase tracking-widest" data-id="element-345">
              Essex Co.
            </div>
            <div className="absolute bottom-6 left-6 text-xs font-bold text-[#F97316]/60 uppercase tracking-widest" data-id="element-346">
              Passaic Co.
            </div>
            <div className="absolute bottom-6 right-6 text-xs font-bold text-[#7DD3FC]/60 uppercase tracking-widest" data-id="element-347">
              Hudson Co.
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}